import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Tables } from "@/types/database";
import { totalGrossSpend } from "@/lib/spend";
import { sumExpensesByPhaseId, sumPlannedByPhaseId } from "@/lib/utils/budget";
import { throwIfError } from "./_supabaseErrors";
import { excludeTestData } from "./testData";

export type Phase = Tables<"phases">;
export type Task = Tables<"tasks">;
export type Expense = Tables<"expenses">;

export async function getSpentTotal(): Promise<number> {
  // * kell: is_test a 044 után jön; hiányzó mezőnél excludeTestData átengedi a sort.
  const { data, error } = await supabase.from("expenses").select("*");
  throwIfError(error);
  return totalGrossSpend(excludeTestData(data));
}

export async function getActivePhaseCount(): Promise<number> {
  const { data, error } = await supabase.from("phases").select("*").eq("status", "active");
  throwIfError(error);
  return excludeTestData(data).length;
}

export const activePhaseCountQueryOptions = () =>
  queryOptions({
    queryKey: ["dashboard", "activePhaseCount"] as const,
    queryFn: getActivePhaseCount
  });

export async function getActivePhase(): Promise<Phase | null> {
  const { data, error } = await supabase
    .from("phases")
    .select("*")
    .eq("status", "active")
    .order("order_index", { ascending: true });
  throwIfError(error);
  return excludeTestData(data)[0] ?? null;
}

export async function getActivePhaseProgress(): Promise<{
  phase: Phase;
  taskTotal: number;
  taskDone: number;
  budgetActual: number;
  budgetPlanned: number;
} | null> {
  const phase = await getActivePhase();
  if (!phase) return null;

  const [{ data: tasks, error: tErr }, { data: expenses, error: eErr }] = await Promise.all([
    supabase.from("tasks").select("*").eq("phase_id", phase.id),
    supabase.from("expenses").select("*")
  ]);
  throwIfError(tErr);
  throwIfError(eErr);

  const list = excludeTestData(tasks);
  const expenseList = excludeTestData(expenses);
  const taskTotal = list.length;
  const taskDone = list.filter((t) => t.status === "done").length;
  const taskPhaseById = new Map(list.map((t) => [t.id, phase.id] as const));
  const spentByPhase = sumExpensesByPhaseId(expenseList, taskPhaseById);
  const budgetActual = spentByPhase.get(phase.id) ?? 0;
  const budgetPlanned = sumPlannedByPhaseId(list).get(phase.id) ?? 0;

  return { phase, taskTotal, taskDone, budgetActual, budgetPlanned };
}

export async function getNextTasks(limit = 5): Promise<Task[]> {
  const { data, error } = await supabase
    .from("tasks")
    .select("*")
    .in("status", ["planned", "in_progress", "blocked"])
    .order("order_index", { ascending: true })
    .limit(limit * 3);
  throwIfError(error);
  return excludeTestData(data).slice(0, limit);
}

/** Sum of accepted quote amounts (may overlap with recorded expenses — shown separately). */
export async function getAcceptedQuotesTotal(): Promise<number> {
  // F2.1: ha van commitments tábla, az az elsődleges „Lekötött”
  const fromCommitments = await supabase
    .from("commitments")
    .select("amount_gross, status, is_test")
    .in("status", ["active", "draft"]);
  if (!fromCommitments.error && fromCommitments.data) {
    const sum = excludeTestData(fromCommitments.data).reduce(
      (s, c) => s + (Number(c.amount_gross) || 0),
      0
    );
    if (sum > 0 || fromCommitments.data.length > 0) return sum;
  }

  const { data, error } = await supabase.from("quotes").select("*").eq("status", "accepted");
  throwIfError(error);
  return excludeTestData(data).reduce((s, q) => {
    const v = q.comparable_total ?? q.amount_gross ?? q.amount;
    return s + (typeof v === "number" ? v : 0);
  }, 0);
}

/** Fázisonként: terv / tény / lekötött (elfogadott ajánlatok), nettó + bruttó. */
export async function getBudgetBreakdownByPhase(): Promise<
  Array<{
    phase: Phase;
    planned: number;
    actual_gross: number;
    actual_net: number;
    committed_gross: number;
    committed_net: number;
  }>
> {
  const { data: phasesRaw, error: pErr } = await supabase
    .from("phases")
    .select("*")
    .order("order_index", { ascending: true });
  throwIfError(pErr);
  const phases = excludeTestData(phasesRaw);

  const [{ data: tasksRaw, error: tErr }, { data: expensesRaw, error: eErr }, { data: quotesRaw, error: qErr }] =
    await Promise.all([
      supabase.from("tasks").select("*"),
      supabase.from("expenses").select("*"),
      supabase.from("quotes").select("*, tasks(phase_id)").eq("status", "accepted")
    ]);
  throwIfError(tErr);
  throwIfError(eErr);
  throwIfError(qErr);

  const tasks = excludeTestData(tasksRaw);
  const expenses = excludeTestData(expensesRaw);
  const quotes = excludeTestData(quotesRaw ?? []);

  const taskPhaseById = new Map(tasks.map((t) => [t.id, t.phase_id] as const));
  const plannedBy = sumPlannedByPhaseId(tasks);

  const actualGross = new Map<string, number>();
  const actualNet = new Map<string, number>();
  for (const e of expenses) {
    const phaseId = e.phase_id ?? (e.task_id ? taskPhaseById.get(e.task_id) : null);
    if (!phaseId) continue;
    const gross = typeof e.amount_gross === "number" ? e.amount_gross : Number(e.amount) || 0;
    const net =
      typeof e.amount_net === "number"
        ? e.amount_net
        : Math.round((gross / 1.23) * 100) / 100;
    actualGross.set(phaseId, (actualGross.get(phaseId) ?? 0) + gross);
    actualNet.set(phaseId, (actualNet.get(phaseId) ?? 0) + net);
  }

  const committedGross = new Map<string, number>();
  const committedNet = new Map<string, number>();
  for (const q of quotes) {
    const t = (q as { tasks?: { phase_id?: string | null } | { phase_id?: string | null }[] | null }).tasks;
    const phaseId = Array.isArray(t) ? t[0]?.phase_id : t?.phase_id;
    if (!phaseId) continue;
    const gross =
      typeof q.comparable_total === "number"
        ? q.comparable_total
        : typeof q.amount_gross === "number"
          ? q.amount_gross
          : Number(q.amount) || 0;
    const net =
      typeof q.amount_net === "number" ? q.amount_net : Math.round((gross / 1.23) * 100) / 100;
    committedGross.set(phaseId, (committedGross.get(phaseId) ?? 0) + gross);
    committedNet.set(phaseId, (committedNet.get(phaseId) ?? 0) + net);
  }

  return phases.map((phase) => ({
    phase,
    planned: plannedBy.get(phase.id) ?? 0,
    actual_gross: actualGross.get(phase.id) ?? 0,
    actual_net: actualNet.get(phase.id) ?? 0,
    committed_gross: committedGross.get(phase.id) ?? 0,
    committed_net: committedNet.get(phase.id) ?? 0
  }));
}

export const budgetBreakdownByPhaseQueryOptions = () =>
  queryOptions({
    queryKey: ["dashboard", "budgetBreakdownByPhase"] as const,
    queryFn: getBudgetBreakdownByPhase
  });

/** Sum of task-level actual costs so far. */
export async function getTasksActualBudgetTotal(): Promise<number> {
  const { data, error } = await supabase.from("tasks").select("*");
  throwIfError(error);
  return excludeTestData(data).reduce(
    (s, t) => s + (typeof t.budget_actual === "number" ? t.budget_actual : 0),
    0
  );
}

/** Sum of task-level planned costs (rozpočet-alapú terv az egész projektre). */
export async function getTasksPlannedBudgetTotal(): Promise<number> {
  const { data, error } = await supabase.from("tasks").select("*");
  throwIfError(error);
  return excludeTestData(data).reduce(
    (s, t) => s + (typeof t.budget_planned === "number" ? t.budget_planned : 0),
    0
  );
}

export async function getBudgetByPhase(): Promise<
  Array<{ phase: Phase; actual: number; planned: number }>
> {
  const { data: phasesRaw, error: pErr } = await supabase
    .from("phases")
    .select("*")
    .order("order_index", { ascending: true });
  throwIfError(pErr);
  const phases = excludeTestData(phasesRaw);

  const [{ data: tasksRaw, error: tErr }, { data: expensesRaw, error: eErr }] = await Promise.all([
    supabase.from("tasks").select("*"),
    supabase.from("expenses").select("*")
  ]);
  throwIfError(tErr);
  const tasks = excludeTestData(tasksRaw);
  const expenses = excludeTestData(expensesRaw);
  throwIfError(eErr);

  const taskPhaseById = new Map(tasks.map((t) => [t.id, t.phase_id] as const));
  const by = sumExpensesByPhaseId(expenses, taskPhaseById);
  const plannedBy = sumPlannedByPhaseId(tasks);

  return phases.map((phase) => ({
    phase,
    actual: by.get(phase.id) ?? 0,
    planned: plannedBy.get(phase.id) ?? 0
  }));
}

export const spentTotalQueryOptions = () =>
  queryOptions({ queryKey: ["dashboard", "spentTotal"] as const, queryFn: getSpentTotal });

export const activePhaseQueryOptions = () =>
  queryOptions({ queryKey: ["dashboard", "activePhase"] as const, queryFn: getActivePhase });

export const activePhaseProgressQueryOptions = () =>
  queryOptions({
    queryKey: ["dashboard", "activePhaseProgress"] as const,
    queryFn: getActivePhaseProgress
  });

export const nextTasksQueryOptions = (limit = 5) =>
  queryOptions({
    queryKey: ["dashboard", "nextTasks", { limit }] as const,
    queryFn: () => getNextTasks(limit)
  });

export const budgetByPhaseQueryOptions = () =>
  queryOptions({ queryKey: ["dashboard", "budgetByPhase"] as const, queryFn: getBudgetByPhase });

export const acceptedQuotesTotalQueryOptions = () =>
  queryOptions({
    queryKey: ["dashboard", "acceptedQuotesTotal"] as const,
    queryFn: getAcceptedQuotesTotal
  });

export const tasksActualBudgetTotalQueryOptions = () =>
  queryOptions({
    queryKey: ["dashboard", "tasksActualBudgetTotal"] as const,
    queryFn: getTasksActualBudgetTotal
  });

export const tasksPlannedBudgetTotalQueryOptions = () =>
  queryOptions({
    queryKey: ["dashboard", "tasksPlannedBudgetTotal"] as const,
    queryFn: getTasksPlannedBudgetTotal
  });

